import React, { useState, useEffect } from "react";
import axios from "axios";
import { Filter } from "bad-words";
const filter = new Filter();

export default function MessageBoard() {
  const [showInputs, setShowInputs] = useState(false);
  const [discussions, setDiscussions] = useState([]);
  const [selectedDiscussion, setSelectedDiscussion] = useState(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [message, setMessage] = useState("");
  const [reply, setReply] = useState("");
  const [replyMessage, setReplyMessage] = useState("");
  const [activeReplyFor, setActiveReplyFor] = useState(null);
  const [commentReplyText, setCommentReplyText] = useState("");
  const [activeEditCommentId, setActiveEditCommentId] = useState(null);
  const [editCommentText, setEditCommentText] = useState("");
  const [isEditingDiscussion, setIsEditingDiscussion] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");
  const [likeStatus, setLikeStatus] = useState({ discussionLiked: false, commentLikes: {} });

  const getCurrentUserId = () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      return user?.id || user?._id || null;
    } catch (_) {
      return null;
    }
  };

  useEffect(() => {
    const fetchDiscussions = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/discussion`
        );
        setDiscussions(res.data);
      } catch (err) {
        console.error(err);
        setMessage("Error fetching discussions.");
      }
    };
    fetchDiscussions();
  }, []);

  const fetchDiscussionById = async (discussionId) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/discussion/${discussionId}`
      );
      setSelectedDiscussion(res.data);
      setActiveReplyFor(null);
      setCommentReplyText("");
      setActiveEditCommentId(null);
      setEditCommentText("");
      
      // Fetch like status
      await fetchLikeStatus(discussionId);
    } catch (err) {
      console.error(err);
      setMessage("Error fetching discussion details.");
    }
  };

  const fetchLikeStatus = async (discussionId) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/discussion/${discussionId}/likes`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLikeStatus(res.data);
    } catch (err) {
      console.error("Error fetching like status:", err);
    }
  };


  const handleSubmitDiscussion = async (e) => {
    e.preventDefault();
    if (!title || !content) return setMessage("All fields are required.");

    try {
      if (filter.isProfane(title) || filter.isProfane(content)) {
        setMessage("Please remove any profanity before submitting.");
        setTimeout(() => setMessage(""), 2000);
        return;
      }
      const token = localStorage.getItem("token");
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/discussion`,
        { title, content },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage("Discussion created successfully!");
      setTimeout(() => setMessage(""), 2000);
      setTitle("");
      setContent("");
      setShowInputs(false);

      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/discussion`
      );
      setDiscussions(res.data);
    } catch (err) {
      console.error(err);
      setMessage("Error creating discussion.");
    }
  };

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;

    try {
      if (filter.isProfane(reply)) {
        setReplyMessage("Please remove any profanity before submitting.");
        setTimeout(() => setReplyMessage(""), 2000);
        return;
      }
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/discussion/${selectedDiscussion._id}/comment`,
        { content: reply },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSelectedDiscussion((prev) => ({
        ...prev,
        comments: [...(prev.comments || []), res.data.comment],
      }));
      setReply("");
    } catch (err) {
      console.error(err);
      setReplyMessage("Error creating reply.");
    }
  };

  const handleReplyToComment = async (commentId) => {
    if (!commentReplyText.trim()) return;

    try {
      if (filter.isProfane(commentReplyText)) {
        setReplyMessage("Please remove any profanity before submitting.");
        setTimeout(() => setReplyMessage(""), 2000);
        return;
      }
      const token = localStorage.getItem("token");
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/discussion/${selectedDiscussion._id}/comment/${commentId}/reply`,
        { content: commentReplyText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchDiscussionById(selectedDiscussion._id);
      setActiveReplyFor(null);
      setCommentReplyText("");
    } catch (err) {
      console.error(err);
      setReplyMessage("Error creating reply.");
    }
  };

  const handleEditDiscussion = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/discussion/${selectedDiscussion._id}`,
        { title: editTitle, content: editContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchDiscussionById(selectedDiscussion._id);
      setIsEditingDiscussion(false);
    } catch (err) {
      console.error(err);
      setMessage("Error updating discussion.");
    }
  };

  const handleStartEditDiscussion = () => {
    setIsEditingDiscussion(true);
    setEditTitle(selectedDiscussion.title);
    setEditContent(selectedDiscussion.content);
  };

  const handleEditComment = async (commentId) => {
    if (!editCommentText.trim()) return;
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/discussion/${selectedDiscussion._id}/comment/${commentId}`,
        { content: editCommentText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchDiscussionById(selectedDiscussion._id);
      setActiveEditCommentId(null);
      setEditCommentText("");
    } catch (err) {
      console.error(err);
      setReplyMessage("Error updating comment.");
    }
  };

  const handleDeleteComment = async (commentId) => {
    const confirmDelete = window.confirm("Delete this comment?");
    if (!confirmDelete) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/discussion/${selectedDiscussion._id}/comment/${commentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchDiscussionById(selectedDiscussion._id);
    } catch (err) {
      console.error(err);
      setReplyMessage("Error deleting comment.");
    }
  };

  const renderComments = (comments, depth = 0) => {
    if (!comments || comments.length === 0) return null;
    const userId = getCurrentUserId();
    
    return (
      <div className={depth > 0 ? "ml-4 border-l pl-4" : ""}>
        {comments.map((comment) => (
          <div key={comment._id} className="border p-3 rounded bg-gray-50 mb-3">
            <p className="text-sm text-gray-600 mb-1">
              <strong>{comment.author?.name || comment.author?.email || "Unknown"}</strong> •{" "}
              {new Date(comment.createdAt).toLocaleString()}
            </p>
            {activeEditCommentId === comment._id ? (
              <div>
                <textarea
                  className="w-full border rounded p-2 h-24"
                  value={editCommentText}
                  onChange={(e) => setEditCommentText(e.target.value)}
                />
                <div className="flex gap-2 mt-2">
                  <button
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded"
                    onClick={() => handleEditComment(comment._id)}
                  >
                    Save
                  </button>
                  <button
                    className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-3 py-1 rounded"
                    onClick={() => {
                      setActiveEditCommentId(null);
                      setEditCommentText("");
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-gray-800 whitespace-pre-line">
                {comment.content}
              </p>
            )}

            <div className="flex gap-3 mt-2 text-sm items-center">
              <button
                onClick={() => handleLikeComment(comment._id)}
                className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                  likeStatus.commentLikes[comment._id]
                    ? "bg-red-100 text-red-600 hover:bg-red-200"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                <svg 
                  className="w-4 h-4" 
                  fill={likeStatus.commentLikes[comment._id] ? "currentColor" : "none"} 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                {comment.likeCount || 0}
              </button>
              <button
                className="text-blue-600 hover:underline"
                onClick={() => {
                  setActiveReplyFor(comment._id);
                  setCommentReplyText("");
                }}
              >
                Reply
              </button>
              {/* Only comment authors can delete their own comments */}
              {comment.author?._id === userId && (
                <button
                  className="text-red-600 hover:underline"
                  onClick={() => handleDeleteComment(comment._id)}
                >
                  Delete
                </button>
              )}
              {/* Only comment authors can edit their own comments */}
              {comment.author?._id === userId && (
                <button
                  className="text-gray-700 hover:underline"
                  onClick={() => {
                    setActiveEditCommentId(comment._id);
                    setEditCommentText(comment.content);
                  }}
                >
                  Edit
                </button>
              )}
            </div>

            {activeReplyFor === comment._id && (
              <div className="mt-3">
                <textarea
                  className="w-full border rounded p-2 h-24"
                  value={commentReplyText}
                  onChange={(e) => setCommentReplyText(e.target.value)}
                  placeholder="Type your reply..."
                />
                <div className="flex gap-2 mt-2">
                  <button
                    className="bg-[#061941] hover:bg-[#09276b] text-white px-3 py-1 rounded"
                    onClick={() => handleReplyToComment(comment._id)}
                  >
                    Submit Reply
                  </button>
                  <button
                    className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-3 py-1 rounded"
                    onClick={() => {
                      setActiveReplyFor(null);
                      setCommentReplyText("");
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {renderComments(comment.replies, depth + 1)}
          </div>
        ))}
      </div>
    );
  };

  const handleLikeDiscussion = async () => {
    if (!selectedDiscussion) return;
    
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/discussion/${selectedDiscussion._id}/like`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update discussion like count
      setSelectedDiscussion(prev => ({
        ...prev,
        likeCount: res.data.likeCount
      }));
      
      // Update like status
      setLikeStatus(prev => ({
        ...prev,
        discussionLiked: res.data.liked
      }));
    } catch (err) {
      console.error("Error liking discussion:", err);
    }
  };

  const handleLikeComment = async (commentId) => {
    if (!selectedDiscussion) return;
    
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/discussion/${selectedDiscussion._id}/comment/${commentId}/like`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update comment like count in the discussion
      const updateCommentLikeCount = (comments) => {
        return comments.map(comment => {
          if (comment._id === commentId) {
            return { ...comment, likeCount: res.data.likeCount };
          }
          if (comment.replies && comment.replies.length > 0) {
            return { ...comment, replies: updateCommentLikeCount(comment.replies) };
          }
          return comment;
        });
      };
      
      setSelectedDiscussion(prev => ({
        ...prev,
        comments: updateCommentLikeCount(prev.comments)
      }));
      
      // Update like status
      setLikeStatus(prev => ({
        ...prev,
        commentLikes: {
          ...prev.commentLikes,
          [commentId]: res.data.liked
        }
      }));
    } catch (err) {
      console.error("Error liking comment:", err);
    }
  };

  const handleDeleteDiscussion = async () => {
    if (!selectedDiscussion) return;

    const confirmDelete = window.confirm(
      "Are you sure you want to delete this discussion?"
    );
    if (!confirmDelete) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/discussion/${selectedDiscussion._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessage("Discussion deleted successfully!");
      setTimeout(() => setMessage(""), 2000);

      // Refresh discussion list
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/discussion`
      );
      setDiscussions(res.data);

      // Go back to main list
      setSelectedDiscussion(null);
    } catch (err) {
      console.error(err);
      setMessage("Error deleting discussion.");
      setTimeout(() => setMessage(""), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-center text-[#061941] mb-8">
          Discussion Board
        </h1>

        {/* --- Discussion List View --- */}
        {!selectedDiscussion && (
          <>
            <div className="flex justify-between items-center mb-6">
              <button
                className="bg-[#061941] hover:bg-[#09276b] text-white px-5 py-2.5 rounded-lg font-semibold shadow-md transition"
                onClick={() => setShowInputs(!showInputs)}
              >
                {showInputs ? "Cancel" : "Create New Post"}
              </button>
            </div>

            {showInputs && (
              <form
                onSubmit={handleSubmitDiscussion}
                className="bg-white p-6 rounded-lg shadow mb-6 border"
              >
                <h2 className="text-lg font-semibold mb-3">New Discussion</h2>
                <input
                  type="text"
                  placeholder="Enter title..."
                  className="w-full border p-2 rounded mb-3"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
                <textarea
                  placeholder="Write your post..."
                  className="w-full border p-2 rounded mb-4 h-32"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                />
                <button
                  type="submit"
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-semibold"
                >
                  Post Discussion
                </button>
              </form>
            )}

            {message && (
              <p className="text-center text-red-600 mb-4">{message}</p>
            )}
            {discussions.length === 0 && (
              <p className="text-center text-gray-600">No discussions yet.</p>
            )}

            <div className="space-y-4">
              {discussions.map((discussion) => (
                <div
                  key={discussion._id}
                  className="bg-white p-5 rounded-lg shadow hover:shadow-lg transition-all duration-300 cursor-pointer border hover:border-[#F2AF29] hover:ring-2 hover:ring-[#F2AF29]/20 hover:ring-opacity-50"
                  onClick={() => fetchDiscussionById(discussion._id)}
                >
                  <h3 className="text-xl font-semibold text-[#061941]">
                    {discussion.title}
                  </h3>
                  <p className="text-gray-700 mt-1">
                    {discussion.content.slice(0, 100)}...
                  </p>
                  <div className="flex justify-between items-center mt-2">
                    <p className="text-sm text-gray-500">
                      by <strong>{discussion.author?.name || discussion.author?.email || "Unknown"}</strong> •{" "}
                      {new Date(discussion.createdAt).toLocaleString()}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        {discussion.commentCount || 0} comments
                      </span>
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                        {discussion.likeCount || 0} likes
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* --- Single Discussion View --- */}
        {selectedDiscussion && (
          <div className="bg-white p-6 rounded-lg shadow border">
            <div className="flex justify-between items-center mb-4">
              <button
                className="text-blue-600 hover:underline font-medium"
                onClick={() => setSelectedDiscussion(null)}
              >
                ← Back to Discussions
              </button>

              <div className="flex gap-2">
                {selectedDiscussion.author?._id === getCurrentUserId() && (
                  <>
                    {!isEditingDiscussion && (
                      <button
                        onClick={handleStartEditDiscussion}
                        className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg font-semibold shadow-md transition"
                      >
                        Edit
                      </button>
                    )}
                    <button
                      onClick={handleDeleteDiscussion}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold shadow-md transition"
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>
            </div>

            {isEditingDiscussion ? (
              <div className="mb-4">
                <input
                  className="w-full border p-2 rounded mb-3"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                />
                <textarea
                  className="w-full border p-2 rounded mb-3 h-32"
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleEditDiscussion}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setIsEditingDiscussion(false)}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-bold text-[#061941] mb-2">
                  {selectedDiscussion.title}
                </h2>
                <p className="text-gray-600 mb-4">
                  by{" "}
                  <strong>
                    {selectedDiscussion.author?.name || selectedDiscussion.author?.email || "Unknown"}
                  </strong>{" "}
                  • {new Date(selectedDiscussion.createdAt).toLocaleString()}
                </p>
                <div className="bg-gray-50 p-4 rounded mb-6 border">
                  <p className="text-gray-800 whitespace-pre-line">
                    {selectedDiscussion.content}
                  </p>
                </div>
                
                {/* Like button for discussion */}
                <div className="flex items-center gap-2 mb-4">
                  <button
                    onClick={handleLikeDiscussion}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                      likeStatus.discussionLiked
                        ? "bg-red-100 text-red-600 hover:bg-red-200"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    <svg 
                      className="w-5 h-5" 
                      fill={likeStatus.discussionLiked ? "currentColor" : "none"} 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                    {likeStatus.discussionLiked ? "Liked" : "Like"} ({selectedDiscussion.likeCount || 0})
                  </button>
                </div>
              </>
            )}

            <h3 className="text-lg font-semibold mb-2">Comments</h3>
            <div className="space-y-3">
              {selectedDiscussion.comments &&
              selectedDiscussion.comments.length > 0 ? (
                renderComments(selectedDiscussion.comments)
              ) : (
                <p className="text-gray-500">
                  No comments yet. Be the first to reply!
                </p>
              )}
            </div>

            {/* Reply Form */}
            <form onSubmit={handleSubmitReply} className="mt-6 border-t pt-4">
              <label className="block font-medium mb-2 text-gray-700">
                Write a reply:
              </label>
              {replyMessage && (
                <p
                  className={`mb-3 text-sm ${
                    replyMessage.includes("successfully")
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {replyMessage}
                </p>
              )}
              <textarea
                className="w-full border rounded p-2 h-24 focus:ring-2 focus:ring-[#061941] focus:outline-none"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Type your comment..."
              />
              <button
                type="submit"
                className="mt-3 bg-[#061941] hover:bg-[#09276b] text-white px-4 py-2 rounded font-semibold shadow"
              >
                Submit Reply
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
